import { AlertTriangle, ArrowLeft, Bug, Copy, Download, Pause, RefreshCw } from "lucide-react";
import { Fragment, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Button, ConfirmDialog, cx, ErrorNote, Spinner, toast } from "../../components/ui";
import { api, useApi, type Me } from "../../lib/api";
import { useIdle } from "../../lib/idle";
import { InforActivity } from "./InforActivity";
import { keyLabel, NODE_LABEL, NODE_TONE, STATUS_LABEL, STATUS_TONE, time, when, type Run, type RunNode, type RunPath, type RunReport } from "./types";

interface Detail {
  run: Run;
  events: { n: number; at: string; level: "info" | "warn" | "error"; message: string }[];
  stale: boolean;
  /** The organization has the Infor activity panel switched on. */
  activity?: boolean;
  steps: { id: string; kind?: string; class?: string; action?: string; submit?: string; breakAfter: boolean; radar?: { op: string } }[];
}
type Action = "continue" | "retry" | "keepChecking" | "skip" | "stop" | "nudge" | "stopBranch";

/** Status screen (design §8.6): the run's steps, reports and log, refreshed every few seconds while it's open. */
export default function JaRunStatus({ me }: { me: Me }) {
  const { runId = "" } = useParams();
  const { data, error, reload, setData } = useApi<Detail>(`/apps/ja/runs/${runId}`);
  const [open, setOpen] = useState<number | null>(null);
  const [confirm, setConfirm] = useState<{ action: Action; title: string; text: string; danger?: boolean; body?: object } | null>(null);
  const canRun = !!me.apps?.find((a) => a.id === "ja")?.roles.includes("run");
  const run = data?.run;
  const idle = useIdle();

  // Refreshes while the run is open and someone is looking; an idle browser polls nothing, and catches up on return.
  useEffect(() => {
    if (!run?.open || idle) return;
    void reload();
    const t = setInterval(() => void reload(), 5000);
    return () => clearInterval(t);
  }, [run?.open, idle, reload]);

  const act = async (action: Action, body: object = {}) => {
    const r = await api<{ run: Run }>(`/apps/ja/runs/${runId}/actions`, { body: { action, ...body } });
    setData((d) => (d ? { ...d, run: r.run } : d));
    void reload();
  };
  const tryAct = (action: Action) => act(action).catch((e) => toast((e as Error).message, "error"));

  if (error && !data) return <ErrorNote>{error}</ErrorNote>;
  if (!data || !run) return <Spinner />;
  const paused = run.pause ? run.nodes[run.pause.node] : undefined;
  const stepOf = (n: RunNode) => data.steps.find((x) => x.id === n.stepId);
  const done = run.nodes.filter((n) => n.state === "done").length;
  const total = run.nodes.filter((n) => n.state !== "notApplicable").length;

  return (
    <div className="space-y-6">
      <div>
        <Link to="/apps/ja/runs" className="inline-flex items-center gap-1 text-sm text-accent-text hover:underline mb-3">
          <ArrowLeft className="size-4" aria-hidden /> Runs
        </Link>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-bold text-ink">
            {run.name} <span className="text-muted font-normal text-base">· {run.type} v{run.version} · run {run.seq}</span>
          </h1>
          <span className={cx("text-xs font-semibold rounded-full px-2.5 py-1", run.status === "done" && run.exceptions ? "bg-warn-soft text-warn" : STATUS_TONE[run.status])}>
            {run.status === "done" && run.exceptions ? `Complete with exceptions (${run.exceptions})` : STATUS_LABEL[run.status]}
          </span>
        </div>
        <p className="text-sm text-muted">
          {keyLabel(run.runKey)} · {done} of {total} steps complete · started {when(run.startedAt)} by {run.startedBy}
          {run.endedAt && ` · ended ${when(run.endedAt)}`}
        </p>
      </div>

      {run.pause?.reason === "break" && paused && (
        <div className="rounded-xl border border-warn bg-warn-soft p-4 flex flex-wrap items-center gap-3">
          <Pause className="size-5 text-warn" aria-hidden />
          <div className="flex-1 min-w-60">
            <div className="font-medium text-ink">Review {paused.jobName} {paused.name}</div>
            <div className="text-sm text-ink-2">Check the step's reports and Infor, then continue to the next step.</div>
          </div>
          {canRun && (
            <>
              <Button variant="primary" onClick={() => void tryAct("continue")}>
                Continue
              </Button>
              <Button onClick={() => setConfirm({ action: "stop", title: "Stop this run?", text: "The run ends here. Nothing already sent to Infor is undone.", danger: true })}>Stop</Button>
            </>
          )}
        </div>
      )}

      {run.pause?.reason === "debug" && paused && <DebugPanel node={paused} message={run.pause.message} canRun={canRun} act={(a, b) => act(a, b).catch((e) => toast((e as Error).message, "error"))} />}

      {run.pause?.reason === "failed" && paused && (
        <div className="rounded-xl border border-bad bg-bad-soft p-4 space-y-3">
          <div className="flex items-start gap-3">
            <AlertTriangle className="size-5 text-bad mt-0.5" aria-hidden />
            <div>
              <div className="font-medium text-ink">
                {paused.jobName} {paused.name} needs a decision
              </div>
              <div className="text-sm text-ink-2">{paused.error ?? run.pause?.message}</div>
              {run.pause?.path && <div className="text-sm text-ink-2">The {run.paths?.find((p) => p.id === run.pause!.path)?.name ?? run.pause.path} branch is critical, so the run holds until you decide.</div>}
              {paused.uncertain && <div className="text-sm text-bad mt-1">Infor may already have done this. Check in Infor before trying again.</div>}
            </div>
          </div>
          {canRun && (
            <div className="flex flex-wrap gap-2">
              {paused.timedOut && (
                <Button variant="primary" onClick={() => void tryAct("keepChecking")}>
                  Keep checking
                </Button>
              )}
              <Button
                variant={paused.timedOut ? "secondary" : "primary"}
                onClick={() =>
                  setConfirm({
                    action: "retry",
                    title: `Send ${paused.name} again?`,
                    text: paused.uncertain ? "Infor may already have received the first request. Only send it again after checking it didn't run." : paused.timedOut ? "The first request may still be running in Infor. Sending again starts a second one." : "The step is sent to Infor again with the same values.",
                    danger: paused.uncertain || paused.timedOut,
                    body: paused.uncertain ? { confirm: true } : {},
                  })
                }
              >
                Try again
              </Button>
              {paused.rerun && paused.rerun.value !== true && (
                <Button
                  onClick={() =>
                    setConfirm({
                      action: "retry",
                      title: `Send ${paused.name} again as a rerun?`,
                      text: `Sends ${paused.rerun!.field} = true, so Infor runs it again even though it ran before${paused.rerun!.flag ? ` (${paused.rerun!.flag} isn't 0)` : ""}.`,
                      body: { rerun: true, ...(paused.uncertain ? { confirm: true } : {}) },
                    })
                  }
                >
                  Try again as a rerun
                </Button>
              )}
              <Button onClick={() => setConfirm({ action: "skip", title: `Skip ${paused.name}?`, text: "The run carries on with the next step as if this one were done." })}>Skip</Button>
              <Button onClick={() => setConfirm({ action: "stop", title: "Stop this run?", text: "The run ends here. Nothing already sent to Infor is undone.", danger: true })}>Stop</Button>
            </div>
          )}
        </div>
      )}

      {run.status === "running" && (
        <div className="flex flex-wrap items-center gap-3 text-sm text-muted">
          <RefreshCw className="size-4 animate-spin" aria-hidden /> Running. This page refreshes every few seconds.
          {data.stale && canRun && (
            <Button onClick={() => void tryAct("nudge")}>Nothing has moved for a while: check now</Button>
          )}
          {canRun && (
            <Button variant="ghost" onClick={() => setConfirm({ action: "stop", title: "Stop this run?", text: "The run stops after the step in progress is recorded. Nothing already sent to Infor is undone, and a step Infor is running carries on there.", danger: true })}>
              Stop
            </Button>
          )}
        </div>
      )}

      {(run.paths?.length ?? 0) > 0 && <Branches run={run} canRun={canRun} act={(action, body) => act(action, body).catch((e) => toast((e as Error).message, "error"))} />}

      <section>
        <h2 className="font-display font-semibold text-ink mb-2">Steps</h2>
        <div className="overflow-x-auto rounded-xl border border-line bg-surface">
          <table className="w-full text-sm">
            <thead className="text-left text-muted border-b border-line">
              <tr>
                <th className="px-3 py-2 font-medium">Job</th>
                <th className="px-3 py-2 font-medium">Step</th>
                <th className="px-3 py-2 font-medium hidden sm:table-cell">Status</th>
                <th className="px-3 py-2 font-medium hidden sm:table-cell">Latest</th>
                <th className="px-3 py-2 font-medium hidden sm:table-cell">Reports</th>
              </tr>
            </thead>
            <tbody>
              {treeOrder(run).map(({ i, depth }) => ({ n: run.nodes[i], i, depth })).map(({ n, i, depth }) => {
                const status = (
                  <>
                    {NODE_LABEL[n.state]}
                    {n.attempt > 1 && <span className="text-xs text-muted font-normal"> · try {n.attempt}</span>}
                    <div className="text-xs text-muted font-normal">{n.state === "running" || n.state === "sending" ? `since ${time(n.submittedAt ?? n.startedAt)}` : n.endedAt ? time(n.endedAt) : ""}</div>
                  </>
                );
                const latest = (
                  <>
                    {n.error ?? n.lastCheck ?? n.note}
                    {n.spawned && n.spawned.length > 0 && (
                      <div className={n.spawned.some((x) => x.state === "failed") ? "text-warn" : "text-muted"}>
                        Background: {n.spawned.length} followed
                        {(() => {
                          const failed = n.spawned!.filter((x) => x.state === "failed").length;
                          const running = n.spawned!.filter((x) => x.state === "running").length;
                          return failed ? `, ${failed} with failures` : running ? `, ${running} still running` : ", all complete";
                        })()}
                      </div>
                    )}
                    {n.waitingOn && <div className="text-muted">{n.waitingOn}</div>}
                    {n.warnings.map((w) => (
                      <div key={w} className="text-warn">
                        ⚠ {w}
                      </div>
                    ))}
                  </>
                );
                const reports = (
                  <>
                    {n.reports.map((r, j) => (
                      <ReportLink key={r.name} runId={run.runId} node={i} index={j} report={r} stepId={n.stepId} job={n.jobName} canAct={canRun && run.status !== "stopped"} onActed={() => void reload()} />
                    ))}
                    {n.radar?.jobId && (
                      <Link to={`/apps/radar/jobs/${n.radar.jobId}`} className="text-accent-text hover:underline">
                        {n.radar.status === "COMPLETED" ? (n.radar.op === "compassExport" ? "Open the CSV in RADAR" : "View the report in RADAR") : "Open in RADAR"}
                      </Link>
                    )}
                  </>
                );
                return (
                <Fragment key={i}>
                  <tr className={cx("border-b border-line align-top cursor-pointer hover:bg-surface-2", run.pause?.node === i && "bg-surface-2", n.state === "notApplicable" && "text-muted")} onClick={() => setOpen(open === i ? null : i)}>
                    <td className="px-3 py-2 font-mono text-xs">{n.jobName}</td>
                    <td className="px-3 py-2" style={depth ? { paddingLeft: `${0.75 + depth * 1.25}rem` } : undefined}>
                      <div className="font-medium flex flex-wrap items-center gap-x-1.5">
                        {depth > 0 && <span className="text-muted" aria-hidden>↳</span>}
                        {n.name}
                        {n.repeat && <span className="text-muted font-normal"> · {n.repeat.loop ? "pass" : "row"} {n.repeat.index} of {n.repeat.of}</span>}
                        {n.path && <span className="rounded bg-accent-soft text-accent-text px-1.5 text-xs font-normal">{run.paths?.find((p) => p.id === n.path)?.name ?? n.path}</span>}
                        {stepOf(n)?.breakAfter && (!n.repeat || n.repeat.last) && <Pause className="size-3.5 text-warn" aria-label="Pauses for review" />}
                      </div>
                      <div className="text-xs text-muted font-mono break-all">{stepOf(n)?.kind === "compass" ? "Compass SQL" : stepOf(n)?.kind === "radar" ? `RADAR ${stepOf(n)?.radar?.op ?? ""}` : stepOf(n)?.class ? `${stepOf(n)!.class}.${stepOf(n)!.action}` : n.stepId}</div>
                      {n.compass?.queryId && <div className="text-xs text-muted">query <IdCopy value={n.compass.queryId} /></div>}
                      {n.radar?.jobId && <div className="text-xs text-muted">RADAR job <IdCopy value={n.radar.jobId} /></div>}
                      {/* Phones: the status, latest and reports under the step instead of in their own columns. */}
                      <div className="sm:hidden mt-1.5 space-y-1">
                        <div className={cx("font-medium", NODE_TONE[n.state])}>{status}</div>
                        <div className="text-xs text-ink-2 break-words">{latest}</div>
                        <div className="text-xs" onClick={(e) => e.stopPropagation()}>
                          {reports}
                        </div>
                      </div>
                    </td>
                    <td className={cx("px-3 py-2 font-medium whitespace-nowrap hidden sm:table-cell", NODE_TONE[n.state])}>{status}</td>
                    <td className="px-3 py-2 text-xs text-ink-2 max-w-md hidden sm:table-cell">{latest}</td>
                    <td className="px-3 py-2 text-xs hidden sm:table-cell" onClick={(e) => e.stopPropagation()}>
                      {reports}
                    </td>
                  </tr>
                  {open === i && (
                    <tr className="border-b border-line bg-surface-2">
                      <td className="hidden sm:table-cell" />
                      <td colSpan={4} className="px-3 py-3 text-xs space-y-2 break-words">
                        <NodeDetail n={n} />
                      </td>
                    </tr>
                  )}
                </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-muted mt-1">Select a step to see what was sent.</p>
      </section>

      {data.activity && <InforActivity runId={runId} live={run.open} moved={run.updatedAt} idle={idle} />}

      <section>
        <h2 className="font-display font-semibold text-ink mb-2">Log</h2>
        <ol className="rounded-xl border border-line bg-surface divide-y divide-line text-sm max-h-96 overflow-y-auto">
          {[...data.events].reverse().map((e) => (
            <li key={e.n} className="px-4 py-1.5 flex gap-3">
              <span className="text-xs text-muted w-20 shrink-0 tabular">{time(e.at)}</span>
              <span className={cx(e.level === "error" ? "text-bad" : e.level === "warn" ? "text-warn" : "text-ink-2")}>{e.message}</span>
            </li>
          ))}
        </ol>
      </section>

      <section>
        <h2 className="font-display font-semibold text-ink mb-2">Values</h2>
        <dl className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1 text-sm rounded-xl border border-line bg-surface p-4">
          {Object.entries(run.values)
            .filter(([, v]) => v)
            .map(([k, v]) => (
              <div key={k} className="flex gap-2">
                <dt className="text-muted font-mono text-xs pt-0.5">{k}</dt>
                <dd className="text-ink">{v}</dd>
              </div>
            ))}
        </dl>
      </section>

      {confirm && (
        <ConfirmDialog title={confirm.title} confirm={confirm.action === "stop" ? "Stop run" : confirm.action === "retry" ? "Send again" : confirm.action === "skip" ? "Skip step" : "OK"} danger={confirm.danger} onConfirm={() => act(confirm.action, confirm.body)} onClose={() => setConfirm(null)}>
          <p className="text-sm text-ink-2">{confirm.text}</p>
        </ConfirmDialog>
      )}
    </div>
  );
}

function NodeDetail({ n }: { n: RunNode }) {
  return (
    <>
      {n.rerun && (
        <p className="text-ink-2">
          Rerun ({n.rerun.mode === "auto" ? "Auto" : n.rerun.mode === "yes" ? "Yes" : "No"}): {n.rerun.value === undefined ? "decided just before sending" : `${n.rerun.field} = ${n.rerun.value}${n.rerun.why ? `, ${n.rerun.why}` : ""}`}
        </p>
      )}
      {n.checkNote && <p className="text-ink-2">How this step is checked: {n.checkNote} (from Waypoint's reading of the action).</p>}
      {n.requestId && !n.compass && !n.radar && <p>Infor request {n.requestId}</p>}
      {n.watch && (
        <div>
          <p className="text-ink-2">Background work this action starts (from Waypoint): {n.watch.join(", ")}</p>
          {(n.spawned ?? []).map((sp) => (
            <p key={sp.id} className={sp.state === "failed" ? "text-warn" : "text-muted"}>
              {sp.cls}.{sp.action} ({sp.name}): {sp.state === "running" ? "running" : sp.state === "failed" ? "finished with failures" : sp.state === "unwatched" ? "no longer watched" : "complete"}
              {sp.message ? ` · ${sp.message}` : ""}
            </p>
          ))}
        </div>
      )}
      {n.workunit && <p>IPA work unit {n.workunit}</p>}
      {n.baseline && n.refreshedAt !== undefined && <p>Replication: LastRefreshStamp {n.baseline} before, {n.refreshedAt || "not yet moved"} after.</p>}
      {n.compass && (
        <div>
          <p>
            Compass query {n.compass.queryId ? <IdCopy value={n.compass.queryId} /> : "(not submitted)"} · {n.compass.status ?? "not started"} · SQL library {n.compass.entry} v{n.compass.version}
          </p>
          <table className="mt-1">
            <tbody>
              {Object.entries(n.compass.params).map(([k, v]) => (
                <tr key={k}>
                  <td className="pr-4 py-0.5 font-mono">{k}</td>
                  <td className="py-0.5 text-ink-2">{v || <span className="text-muted">(blank)</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {n.radar && (
        <div>
          <p>
            RADAR job <IdCopy value={n.radar.jobId} /> ({n.radar.op}) · {n.radar.status ?? "starting"}
            {n.radar.queryId ? <> · from query <IdCopy value={n.radar.queryId} /></> : null}
          </p>
          {n.radar.push?.printManagerDocumentId && (
            <p>
              Print Manager report document <IdCopy value={n.radar.push.printManagerDocumentId} />
              {n.radar.push.userReportId ? <> · user report <IdCopy value={n.radar.push.userReportId} /></> : null}
              {n.radar.push.distributed ? " · distributed" : ""}
            </p>
          )}
          {n.radar.usage && <p className="text-muted">Tokens: input {String(n.radar.usage.input ?? "?")}, output {String(n.radar.usage.output ?? "?")}</p>}
        </div>
      )}
      {n.checks > 0 && <p className="text-muted">Checked {n.checks} times.</p>}
      {n.fields && (
        <table className="w-full">
          <tbody>
            {n.fields.map((f) => (
              <tr key={f.name} className="align-top">
                <td className="pr-4 py-0.5 font-mono whitespace-nowrap">{f.name}</td>
                <td className="py-0.5 text-ink-2 break-all">{f.value.length > 300 ? `${f.value.slice(0, 300)}…` : f.value || <span className="text-muted">(blank)</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {n.reports.map((r) => r.archive && <p key={r.name}>{r.name}: archive {r.archive.state === "done" ? `saved to ${r.archive.key}` : r.archive.state === "failed" ? `failed (${r.archive.error})` : "pending"}</p>)}
    </>
  );
}

const PATH_LABEL: Record<RunPath["state"], string> = { notStarted: "Not started", running: "Running", paused: "Waiting at a break", attention: "Needs a decision", done: "Done", skipped: "Skipped", stopped: "Stopped" };
const PATH_TONE: Record<RunPath["state"], string> = { notStarted: "text-muted", running: "text-accent-text", paused: "text-warn", attention: "text-bad", done: "text-good", skipped: "text-muted", stopped: "text-muted" };

/** The run's step branches, each with its own state and decisions (docs/phase4-plan.md §3c; the tree view is checkpoint 4). */
function Branches({ run, canRun, act }: { run: Run; canRun: boolean; act: (action: Action, body: object) => Promise<void> }) {
  return (
    <section>
      <h2 className="font-display font-semibold text-ink mb-2">Branches</h2>
      <ul className="rounded-xl border border-line bg-surface divide-y divide-line text-sm">
        {run.paths!.map((p) => {
          const node = p.pause ? run.nodes[p.pause.node] : undefined;
          const live = !["done", "skipped", "stopped"].includes(p.state);
          return (
            <li key={p.id} className="px-4 py-2 space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-ink">{p.name}</span>
                <span className="text-xs text-muted">{p.critical ? "critical" : "non-critical"} · starts after {run.nodes.find((n) => n.stepId === p.startedBy)?.name ?? p.startedBy}</span>
                <span className={cx("text-xs font-medium", PATH_TONE[p.state])}>{PATH_LABEL[p.state]}</span>
                <span className="text-xs text-muted">
                  {p.nodes.filter((i) => run.nodes[i].state === "done").length} of {p.nodes.filter((i) => run.nodes[i].state !== "notApplicable").length} steps
                </span>
              </div>
              {p.pause && p.pause.reason !== "debug" && <div className={cx("text-xs", p.pause.reason === "failed" ? "text-bad" : "text-warn")}>{p.pause.reason === "failed" ? node?.error ?? p.pause.message : p.pause.message}</div>}
              {p.pause?.reason === "debug" && node && <DebugPanel node={node} message={p.pause.message} canRun={canRun && live} act={(a, b) => act(a, { ...b, path: p.id })} />}
              {canRun && live && (
                <div className="flex flex-wrap gap-2">
                  {p.pause?.reason === "break" && (
                    <Button variant="primary" onClick={() => void act("continue", { path: p.id })}>
                      Continue
                    </Button>
                  )}
                  {p.pause?.reason === "failed" && node?.state === "failed" && (
                    <Button variant="primary" onClick={() => void act("retry", { path: p.id, ...(node.uncertain ? { confirm: true } : {}) })}>
                      Try again
                    </Button>
                  )}
                  {p.pause?.reason === "failed" && <Button onClick={() => void act("skip", { path: p.id })}>Skip the step</Button>}
                  <Button variant="ghost" onClick={() => void act("stopBranch", { path: p.id })}>
                    Stop branch
                  </Button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function ReportLink({ runId, node, index, report, stepId, job, canAct, onActed }: { runId: string; node: number; index: number; report: RunReport; stepId: string; job?: string; canAct: boolean; onActed: () => void }) {
  const [busy, setBusy] = useState(false);
  // A report on its own branch: a failed one can be tried again, or let go (it then counts as finished).
  const act = async (action: "retryReport" | "skipReport") => {
    setBusy(true);
    try {
      await api(`/apps/ja/runs/${runId}/actions`, { body: { action, stepId, job, report: report.name } });
      onActed();
    } catch (e) {
      toast((e as Error).message, "error");
    } finally {
      setBusy(false);
    }
  };
  if (report.state !== "done")
    return (
      <div className={report.state === "failed" ? "text-bad" : "text-muted"} title={report.message}>
        {report.name} · {report.state === "failed" ? "failed" : report.state === "skipped" ? "let go" : report.state === "waiting" ? "to request" : report.state === "locating" ? "finding the file" : "in Infor"}
        {report.state === "failed" && report.message && <span className="block text-bad">{report.message}</span>}
        {report.state === "failed" && canAct && (
          <span className="flex gap-2 mt-0.5">
            <button type="button" className="text-accent-text hover:underline disabled:opacity-50" disabled={busy} onClick={() => void act("retryReport")}>
              Retry report
            </button>
            <button type="button" className="text-muted hover:underline disabled:opacity-50" disabled={busy} onClick={() => void act("skipReport")}>
              Let it go
            </button>
          </span>
        )}
      </div>
    );
  const open = async () => {
    setBusy(true);
    try {
      const { url } = await api<{ url: string }>(`/apps/ja/runs/${runId}/report-link`, { body: { node, report: index } });
      window.location.href = url;
    } catch (e) {
      toast((e as Error).message, "error");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button type="button" className="flex items-center gap-1 text-accent-text hover:underline disabled:opacity-50" disabled={busy} onClick={() => void open()}>
      <Download className="size-3" aria-hidden /> {report.name}
      {report.archive?.state === "done" && <span className="text-muted">(archived)</span>}
    </button>
  );
}

/** The run's steps in tree order (the Status tree view): each step branch's steps right after the step that starts them. */
function treeOrder(run: Run): { i: number; depth: number }[] {
  const out: { i: number; depth: number }[] = [];
  const add = (indices: number[], depth: number) => {
    for (const i of indices) {
      out.push({ i, depth });
      for (const p of run.paths ?? []) if (p.startedBy === run.nodes[i].stepId && (!run.nodes[i].repeat || run.nodes[i].repeat!.last)) add(p.nodes, depth + 1);
    }
  };
  add(
    run.nodes.flatMap((n, i) => (n.path ? [] : [i])),
    0,
  );
  return out;
}


/**
 * Debug mode (docs/phase5-plan.md §7): the step's function stopped at a debugger line. Shows where, the function's
 * variables, its call stack and the Infor calls it made; Continue runs it again past the line.
 */
function DebugPanel({ node, message, canRun, act }: { node: RunNode; message: string; canRun: boolean; act: (action: "continue" | "skip", body?: object) => Promise<unknown> | void }) {
  const d = node.debug;
  const call = (c: string) => {
    try {
      const x = JSON.parse(c) as { kind: string; className: string; name?: string };
      return `${x.kind === "action" ? "action" : "read"} ${x.className}${x.name ? `.${x.name}` : ""}`;
    } catch {
      return c;
    }
  };
  return (
    <div className="rounded-xl border border-accent bg-accent-soft p-4 space-y-3 text-sm">
      <div className="flex flex-wrap items-start gap-3">
        <Bug className="size-5 text-accent-text mt-0.5" aria-hidden />
        <div className="flex-1 min-w-60">
          <div className="font-medium text-ink">{message}</div>
          <div className="text-ink-2">Nothing is sent for this step until you continue. Continue runs the function again past this line; Infor answers it already had are reused.</div>
        </div>
        {canRun && (
          <div className="flex flex-wrap gap-2">
            <Button variant="primary" onClick={() => void act("continue")}>
              Continue
            </Button>
            <Button onClick={() => void act("continue", { debugOff: true })}>Continue without stopping</Button>
            <Button variant="ghost" onClick={() => void act("skip")}>
              Skip step
            </Button>
          </div>
        )}
      </div>
      {d && (
        <div className="grid gap-3 lg:grid-cols-2">
          <div>
            <div className="text-xs font-medium text-ink-2 mb-1">Variables in {d.at.fn}</div>
            <table className="w-full text-xs bg-surface rounded-lg">
              <tbody>
                {Object.entries(d.at.vars).map(([k, v]) => (
                  <tr key={k} className="border-b border-line last:border-0 align-top">
                    <td className="px-2 py-1 font-mono whitespace-nowrap">{k}</td>
                    <td className="px-2 py-1 font-mono break-all text-ink-2">{v}</td>
                  </tr>
                ))}
                {!Object.keys(d.at.vars).length && (
                  <tr>
                    <td className="px-2 py-1 text-muted">No variables yet</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="space-y-2">
            <div>
              <div className="text-xs font-medium text-ink-2 mb-1">Infor calls so far ({d.calls?.length ?? 0})</div>
              <ol className="text-xs font-mono bg-surface rounded-lg px-2 py-1 space-y-0.5">
                {(d.calls ?? []).map((c, i) => (
                  <li key={i}>{call(c)}</li>
                ))}
                {!d.calls?.length && <li className="text-muted font-sans">None</li>}
              </ol>
            </div>
            <details className="text-xs">
              <summary className="cursor-pointer text-ink-2">Call stack</summary>
              <pre className="mt-1 bg-surface rounded-lg p-2 whitespace-pre-wrap break-words font-mono">{d.at.stack || "(not available)"}</pre>
            </details>
          </div>
        </div>
      )}
    </div>
  );
}

/** An id with a copy button (design §15.9.5: query ids and RADAR job ids in a live run). */
function IdCopy({ value }: { value: string }) {
  return (
    <span className="inline-flex items-center gap-1 align-middle" onClick={(e) => e.stopPropagation()}>
      <code className="bg-surface-2 rounded px-1">{value}</code>
      <button type="button" className="text-muted hover:text-ink" title="Copy" aria-label="Copy" onClick={() => void navigator.clipboard.writeText(value).then(() => toast("Copied"))}>
        <Copy className="size-3" />
      </button>
    </span>
  );
}
